'use client';

import { useQuery } from '@tanstack/react-query';
import { useCreateListing } from '../_context';
import Image from 'next/image';
import { getMainCatsClient } from '@util/firestore/categories';
import { useAuth } from 'context/AuthContext';

export default function SelectListingSport() {
  const { form, setCurrentStep, product, categoryInfo, setCategoryInfo } =
    useCreateListing();
  const { userDoc } = useAuth();

  const { data: sports } = useQuery({
    queryKey: ['sports'],
    queryFn: () => getMainCatsClient(),
  });

  const otherCats = ['Casual', 'Collectibles', 'Giveaways'];

  const errors = form.formState.errors.ride_types?.message;
  return (
    <div className="flex w-full flex-col justify-between px-4 py-16 sm:mx-auto sm:max-w-[100rem] sm:px-0 sm:py-32">
      <div className="flex flex-col gap-y-4">
        <h1 className="text-[2.5rem] font-semibold text-black sm:text-[3rem]">
          Which sport is this item for?
        </h1>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:gap-8">
          {sports
            ?.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
            ?.filter((c) => !otherCats.includes(c.name))
            .map((c, _) => {
              const supportText =
                c.product_cats.ride_type === 'Street'
                  ? 'Cruiser, Street, Adventure'
                  : c.product_cats.ride_type === 'Cycling'
                  ? 'Road Bikes, E-Bikes, Mountain Bikes'
                  : null;

              return (
                <button
                  type="button"
                  className={`flex h-[30rem] w-full cursor-pointer flex-col justify-between overflow-hidden rounded-2xl pb-4 sm:w-[23rem] sm:pb-8 ${
                    c.product_cats.ride_type === product.ride_types?.[0]
                      ? 'border border-brand-secondary bg-inherit'
                      : 'bg-[#F1F1F1] outline-none'
                  }`}
                  key={_}
                  onClick={() => {
                    setCategoryInfo([c]);
                    setCurrentStep(2);
                  }}
                >
                  <div className="mt-16 flex w-full justify-center sm:mt-0 sm:justify-normal">
                    <Image
                      src={c.image ?? ''}
                      alt={c.product_cats.ride_type ?? 'Ride Type Image'}
                      width={200}
                      height={200}
                      className="-ml-10 h-44 w-full object-contain sm:-ml-20 sm:mt-12 sm:h-64 sm:object-cover sm:object-right"
                      priority
                    />
                  </div>

                  <div className="mx-4 flex flex-col items-start gap-y-1 sm:mx-8 sm:gap-y-2">
                    <h3 className="text-[1.8rem] font-semibold text-zinc-800">
                      {c.product_cats.ride_type}
                    </h3>

                    {supportText && (
                      <span className="text-start text-[1.5rem] text-zinc-500">
                        {supportText}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
        </div>

        <h3 className="mt-8 text-[1.8rem] font-semibold text-black sm:text-[2rem]">
          Other categories
        </h3>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:flex sm:flex-row sm:gap-8">
          {sports
            ?.filter(
              (c) =>
                otherCats.includes(c.product_cats.category || '') &&
                (c.product_cats.category !== 'Giveaways' ||
                  userDoc?.roles?.experiences)
            )
            .map((c) => {
              return (
                <button
                  type="button"
                  className={`flex h-[35rem] w-full cursor-pointer flex-col justify-between overflow-hidden rounded-2xl pb-4 sm:w-[23rem] sm:pb-8 ${
                    c.id === categoryInfo[0]?.id
                      ? 'border border-brand-secondary bg-inherit'
                      : 'bg-[#F1F1F1] outline-none'
                  }`}
                  key={c.product_cats.category}
                  onClick={() => {
                    setCategoryInfo([c]);
                    if (c.product_cats.category === 'Casual') {
                      setCurrentStep(2);
                    } else {
                      form.setValue(
                        'ride_types',
                        c.product_cats.ride_type
                          ? [c.product_cats.ride_type]
                          : []
                      );
                      form.setValue('category', c.product_cats.category!);
                      form.setValue('category1', null);
                      form.setValue('category2', null);
                      form.setValue('category3', null);
                      form.setValue('categories', [c.product_cats.category!]);
                      setCurrentStep(3);
                    }
                    window.scrollTo({ top: 0 });
                  }}
                >
                  <Image
                    src={
                      c.product_cats.category === 'Casual'
                        ? `https://cdn.mxlocker.com/assets/support-casual_1920x2560.png`
                        : c.product_cats.category === 'Collectibles'
                        ? `https://cdn.mxlocker.com/assets/support-collectibles_1920x2560.png`
                        : c.image
                    }
                    alt={c.product_cats.category || 'Category Image'}
                    width={500}
                    height={500}
                    className="-ml-16 mt-2 h-[90rem] w-[90rem] object-cover sm:-ml-32 sm:mt-4 sm:h-[120rem] sm:w-[120rem]"
                    priority
                  />

                  <div className="mx-4 flex flex-col items-start gap-y-1 sm:mx-8 sm:gap-y-2">
                    <h3 className="text-[1.8rem] font-semibold text-zinc-800">
                      {c.product_cats.category}
                    </h3>
                  </div>
                </button>
              );
            })}
        </div>
      </div>

      <div className="mt-16 flex w-full flex-col items-center gap-[1rem]">
        {errors && <p className="text-[1.5rem] text-brand-red">{errors}</p>}
        <button
          className="flex w-[25rem] grow items-center justify-center gap-x-2 rounded-[1.5rem] bg-brand-lightest-gray px-6 py-3 text-[1.6rem] text-black hover:bg-brand-light-gray sm:grow-0 sm:px-8 sm:py-4 sm:text-[1.8rem]"
          type="button"
          onClick={() => {
            setCurrentStep(0);
          }}
        >
          Back
        </button>
      </div>
    </div>
  );
}
